import type * as Contentful from 'contentful'
import clsx from 'clsx'

enum AssetTags {
  Screenshot = 'screenshot',
  LowResolution = 'screenshotLowResolution',
  HasBrowserChrome = 'screenshotHasOSChrome',
}

// 50rem = 800px
const contentColumnWidth = 50 * 16

type FigureProps = Contentful.Asset & { dimensions?: boolean }

export const Figure = ({
  metadata,
  fields,
  dimensions = true,
}: FigureProps) => {
  try {
    if (!fields || !('file' in fields))
      throw new Error('Asset does not have file')
    if (fields.file === undefined) throw new Error('Asset does not have file')
    if (!('details' in fields.file) || fields.file.details === undefined)
      throw new Error('Received AssetFields instead of AssetFile')
    if (
      !('image' in fields.file.details) ||
      fields.file.details.image === undefined
    )
      throw new Error('Received Asset without image details')

    const tags = metadata.tags.map((tag) => tag.sys.id)
    const isLowResolution = tags.includes(AssetTags.LowResolution)

    const { height, width } = fields.file.details.image
    const aspectRatio = height / width
    const maxAvailableResolution = isLowResolution
      ? Math.round(width / 2)
      : width
    const computedWidth = Math.min(maxAvailableResolution, contentColumnWidth)
    const computedHeight = Math.round(computedWidth * aspectRatio)

    const dimensionAttributes = dimensions
      ? { height: computedHeight, width: computedWidth }
      : {}

    return (
      <figure className="image">
        <img
          {...dimensionAttributes}
          alt={typeof fields.title === 'string' ? fields.title : ''}
          className={clsx('image', tags)}
          src={`https:${fields.file.url}?w=${computedWidth * 2}&fm=webp`}
        />
      </figure>
    )
  } catch (error) {
    return (
      <figure className="image">
        <figcaption>
          {error instanceof Error ? error.message : 'Could not load image'}
        </figcaption>
      </figure>
    )
  }
}
